import * as React from 'react';
import * as autoBind from 'react-autobind';
import {RouteComponentProps} from 'react-router';
import ReviewService from '../../services/ReviewService/ReviewService';
import AxiosCancelTokenHelper from '../../utils/AxiosCancelTokenHelper/AxiosCancelTokenHelper';
import DashboardContainer from '../Dashboard/DashboardContainer';
import SkeletonPage from '../SkeletonPage/SkeletonPage';
import ErrorMessageModel from '../../types/ErrorMessageModel';
import Strings from '../../globals/Strings';
import Constants, {DashboardFilter, SortMetric} from '../../globals/Constants';
import ClientSideError from '../ClientSideError/ClientSideError';
import ReviewModel from '../../types/ReviewModel';

export default class Dashboard extends React.Component<DashboardLoaderProps, DashboardLoaderState> {
   readonly state: DashboardLoaderState = {errorMessaging: null, isLoadingData: true};
   private loadReviewsCancelHelper: AxiosCancelTokenHelper;

   constructor(props: DashboardLoaderProps) {
      super(props);
      autoBind(this);
   }

   fetchReviews(sortMetric: SortMetric, dashboardFilter: DashboardFilter) {
      const {setPageIsLoading, resetReviews} = this.props;
      setPageIsLoading(true);
      resetReviews();
      this.loadReviewsCancelHelper = new AxiosCancelTokenHelper();
      ReviewService.loadReviews(sortMetric, 0, Constants.initialTakeValue, dashboardFilter, this.loadReviewsCancelHelper, true).then(() => {
         setPageIsLoading(false);
         this.setState({isLoadingData: false});
      }).catch(e => {
         if (e.message === 'Network Error') {
            setPageIsLoading(false);
            this.setState({
               isLoadingData: false,
               errorMessaging: {
                  title: Strings.errors.noNetworkConnection,
                  detail: Strings.errors.failedLoadDashboard
               }
            });
         } else if (!this.loadReviewsCancelHelper.isCancelled) {
            setPageIsLoading(false);
            this.setState({
               isLoadingData: false,
               errorMessaging: {
                  title: Strings.errors.serverError,
                  detail: Strings.errors.serverErrorDetail,
                  statusCode: Constants.statusCodes.serverError
               }
            });
         }
      });
   }

   componentDidMount() {
      const {sortMetric, location, setDashboardFilter, reviews, totalNotOwnedReviewsCount, totalOwnedReviewsCount} = this.props;

      const urlParams = new URLSearchParams(location.search);

      const filter = (urlParams.get('filter') as DashboardFilter) || DashboardFilter.all;
      setDashboardFilter(filter);

      const hasNavigatedDirectlyToOneReviewThenHitBackToDashboardButton = reviews.length === 1 && !totalNotOwnedReviewsCount && !totalOwnedReviewsCount;

      (!reviews.length || hasNavigatedDirectlyToOneReviewThenHitBackToDashboardButton) && this.fetchReviews(sortMetric, filter);

      if (reviews.length) {
         this.setState({isLoadingData: false});
      }
   }

   componentWillUnmount() {
      this.loadReviewsCancelHelper?.cancel();
   }

   componentDidUpdate(prevProps: DashboardLoaderProps) {
      const {sortMetric, reviews, filteredReviews, searchText, totalReviewsCount, location, setSearchText} = this.props;
      const urlParams = new URLSearchParams(location.search);
      const prevUrlParams = new URLSearchParams(prevProps.location.search);

      const updatedSortMetric = prevProps.sortMetric !== sortMetric && reviews?.length !== totalReviewsCount;
      const updatedDashboardFilterAndNoReviewsExist = prevUrlParams.get('filter') !== urlParams.get('filter') && filteredReviews?.length === 0 && !searchText && reviews?.length !== totalReviewsCount;

      if (updatedSortMetric || updatedDashboardFilterAndNoReviewsExist) {
         const filter = (urlParams.get('filter') as DashboardFilter) || DashboardFilter.all;
         setSearchText('');
         this.fetchReviews(sortMetric, filter);
      }
   }

   render() {
      const {errorMessaging, isLoadingData} = this.state;

      if (errorMessaging) {
         return <ClientSideError title={errorMessaging.title} text1={errorMessaging.detail} showHeaderButtons={false} statusCode={errorMessaging.statusCode}/>;
      }
      return this.props.pageIsLoading || isLoadingData ? <SkeletonPage/> : <DashboardContainer/>;
   }
}

export interface DashboardLoaderProps extends RouteComponentProps {
   pageIsLoading: boolean;
   sortMetric: SortMetric;
   totalReviewsCount: number;
   totalOwnedReviewsCount: number;
   totalNotOwnedReviewsCount: number;
   reviews: ReviewModel[];
   filteredReviews: ReviewModel[];
   searchText: string;
   setDashboardFilter: (filter: string) => void;
   setPageIsLoading(isLoading: boolean): void;
   resetReviews(): void;
   setSearchText(searchText: string): void;
}

interface DashboardLoaderState {
   errorMessaging: ErrorMessageModel;
   isLoadingData: boolean;
}
